import {
  DeliveryStatus,
  DmDeliveryStatus,
  type CompletedDeliveriesAssignmentsQuery,
  type DriverAssignmentQuery,
  type DriverAssignmentsQuery,
  type DriverProgressAssignmentQuery,
  type MyDeliveriesAssignmentsQuery,
} from 'services/graphql/types';

const getAdaptedDeliveryStatus = (status: DeliveryStatus): DmDeliveryStatus => {
  switch (status) {
    case DeliveryStatus.Assigned:
      return DmDeliveryStatus.Assigned;
    case DeliveryStatus.InProgress:
      return DmDeliveryStatus.InProgress;
    case DeliveryStatus.Completed:
      return DmDeliveryStatus.Completed;
    case DeliveryStatus.Expired:
      return DmDeliveryStatus.Expired;
    case DeliveryStatus.Unassigned:
      return DmDeliveryStatus.Unassigned;
    default:
      return DmDeliveryStatus.Assigned;
  }
};

const getUnformattedOrderNumber = (orderNumber: string): string => {
  return orderNumber.replace(/[^0-9a-zA-Z]/g, '');
};

export const getAdaptedAssignmentsQuery = (
  data: CompletedDeliveriesAssignmentsQuery | MyDeliveriesAssignmentsQuery | null | undefined
): DriverAssignmentsQuery | undefined => {
  const assignments = data?.me?.driverAccount?.assignments.nodes;

  if (!assignments) {
    return undefined;
  }

  return {
    driverAssignments: assignments.map(assignment => ({
      __typename: 'DmDriverAssignment',
      delivery: {
        __typename: 'DmDelivery',
        dropoffAddress: {
          __typename: 'DmAddress',
          city: assignment.dropoffAddress.city,
          id: assignment.dropoffAddress.id,
          state: assignment.dropoffAddress.state,
          street1: assignment.dropoffAddress.street1,
          street2: assignment.dropoffAddress.street2,
          zip: assignment.dropoffAddress.zip,
        },
        eventAt: assignment.eventAt,
        // NOTE: The legacy API only has the Delivery ID field included here for
        // Apollo caching. Since the new Driver API is simply adapting its data
        // to the legacy API format there's no need for the Delivery ID to be a
        // "real" Delivery ID. That's why the Assignment ID is being set here.
        id: assignment.id,
        orderNumber: getUnformattedOrderNumber(assignment.formattedOrderNumber),
        pickupAddress: {
          __typename: 'DmAddress',
          id: assignment.pickupAddress.id,
          timeZoneIdentifier: assignment.pickupAddress.timeZoneIdentifier,
        },
        status: getAdaptedDeliveryStatus(assignment.status),
      },
      id: assignment.id,
    })),
  };
};

export const getAdaptedAssignmentQuery = (
  data: DriverProgressAssignmentQuery | null | undefined
): DriverAssignmentQuery | undefined => {
  const assignment = data?.me?.driverAccount?.assignment;

  if (!assignment) {
    return undefined;
  }

  return {
    driverAssignment: {
      __typename: 'DmDriverAssignment',
      delivery: {
        __typename: 'DmDelivery',
        deliverySetup: assignment.deliverySetup,
        distanceInMiles: assignment.distanceInMiles,
        driverAskFor: assignment.driverAskFor,
        dropoffAddress: {
          __typename: 'DmAddress',
          city: assignment.dropoffAddress.city,
          id: assignment.dropoffAddress.id,
          instructions: assignment.dropoffAddress.instructions,
          locationName: assignment.dropoffAddress.locationName,
          phone: assignment.dropoffAddress.phone,
          state: assignment.dropoffAddress.state,
          street1: assignment.dropoffAddress.street1,
          street2: assignment.dropoffAddress.street2,
          zip: assignment.dropoffAddress.zip,
        },
        dropoffWindow: assignment.dropoffWindow
          ? {
              __typename: 'DeliveryArrivalWindow',
              endAt: assignment.dropoffWindow.endAt,
              startAt: assignment.dropoffWindow.startAt,
            }
          : null,
        durationInMinutes: assignment.durationInMinutes,
        eventAt: assignment.eventAt,
        id: assignment.deliveryId,
        order: {
          __typename: 'DmOrder',
          orderItems: assignment.orderItems.map(item => ({
            __typename: 'DmOrderItem',
            name: item.name,
            quantity: item.quantity,
          })),
        },
        orderNumber: getUnformattedOrderNumber(assignment.formattedOrderNumber),
        pickupAddress: {
          __typename: 'DmAddress',
          city: assignment.pickupAddress.city,
          id: assignment.pickupAddress.id,
          instructions: assignment.pickupAddress.instructions,
          locationName: assignment.pickupAddress.locationName,
          state: assignment.pickupAddress.state,
          street1: assignment.pickupAddress.street1,
          street2: assignment.pickupAddress.street2,
          zip: assignment.pickupAddress.zip,
        },
        status: getAdaptedDeliveryStatus(assignment.status),
      },
      feedback: assignment.feedback
        ? {
            __typename: 'DmDriverFeedback',
            message: assignment.feedback.message,
          }
        : null,
      id: assignment.id,
    },
  };
};
